import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Account, MatchReviewItem, Page } from '../lib/types';
import { formatDateTime } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Input,
  Loading,
  PageHeader,
  SectionNote,
  Select,
  useToast,
} from '../components/ui';

/**
 * §15 match review: meetings and recordings ingest could not place by itself.
 *
 * The candidates are the accounts the matcher weighed and could not choose
 * between, offered as one click each. When the right account is none of them,
 * any account the reviewer holds can be chosen instead. Until a meeting is
 * placed it counts toward no account's cadence, and a recording is not
 * extracted, which is why the queue exists.
 */

export function MatchReviewPage() {
  const query = useQuery({
    queryKey: ['match-review'],
    queryFn: () => api.get<{ items: MatchReviewItem[] }>('/match-review'),
  });
  const items = query.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Match Review"
        description="Customer meetings that could not be placed on an account automatically."
      />
      <SectionNote>
        Placing a meeting puts it on that account's timeline at the next calendar sync, and queues a
        recording for AI Review.
      </SectionNote>

      {query.isLoading && <Loading />}
      {query.error && <ErrorNotice error={query.error} onRetry={() => query.refetch()} />}
      {query.data && items.length === 0 && <EmptyState title="Nothing to place" />}

      <div className="mt-4 space-y-3">
        {items.map((item) => (
          <MatchCard key={item.id} item={item} />
        ))}
      </div>
    </>
  );
}

function MatchCard({ item }: { item: MatchReviewItem }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [otherAccountId, setOtherAccountId] = useState('');
  const [choosingOther, setChoosingOther] = useState(false);
  const [search, setSearch] = useState('');

  const accounts = useQuery({
    // The list endpoint pages at 200; a search narrows a large book instead.
    queryKey: ['accounts', 'placeable', search],
    queryFn: () => api.get<Page<Account>>('/accounts', { q: search || undefined, limit: 200, sort: 'name' }),
    enabled: choosingOther,
  });

  const done = (message: string) => {
    toast(message);
    void queryClient.invalidateQueries({ queryKey: ['match-review'] });
    void queryClient.invalidateQueries({ queryKey: ['ai-proposals'] });
  };

  const place = useMutation({
    mutationFn: (accountId: string) => api.post(`/match-review/${item.id}/place`, { accountId }),
    onSuccess: () => done('Meeting placed'),
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(`/match-review/${item.id}/dismiss`, {}),
    onSuccess: () => done('Marked as not a customer meeting'),
  });
  const busy = place.isPending || dismiss.isPending;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone="neutral">{item.sourceType === 'fireflies' ? 'Recording' : 'Calendar'}</Badge>
            {item.isBusinessReview && <Badge tone="info">Business review</Badge>}
            <span className="text-sm font-medium text-ink">{item.subject ?? 'Untitled meeting'}</span>
          </div>
          <p className="mt-1 text-xs text-ink-2">
            {formatDateTime(item.occurredAt)}
            {item.mailboxes.length > 0 && <> · in {item.mailboxes.join(', ')}</>}
            {item.url && (
              <>
                {' · '}
                <a href={item.url} target="_blank" rel="noreferrer" className="underline">
                  open
                </a>
              </>
            )}
          </p>
        </div>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => dismiss.mutate()}>
          Not a customer meeting
        </Button>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {item.candidates.map((candidate) => (
          <Button key={candidate.id} size="sm" disabled={busy} onClick={() => place.mutate(candidate.id)}>
            {candidate.name}
            <span className="ml-1.5 text-muted">{candidate.enterpriseName}</span>
          </Button>
        ))}
        {!choosingOther ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => setChoosingOther(true)}>
            {item.candidates.length ? 'Another account…' : 'Choose account…'}
          </Button>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search accounts" className="w-44" />
            <Select value={otherAccountId} onChange={(event) => setOtherAccountId(event.target.value)} className="w-72">
              <option value="">{accounts.isLoading ? 'Loading accounts…' : 'Choose an account'}</option>
              {(accounts.data?.data ?? []).map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} · {account.enterpriseName}
                </option>
              ))}
            </Select>
            <Button size="sm" variant="primary" disabled={busy || !otherAccountId} onClick={() => place.mutate(otherAccountId)}>
              Place
            </Button>
          </div>
        )}
      </div>

      {(place.error ?? dismiss.error) != null && (
        <div className="mt-2">
          <ErrorNotice error={place.error ?? dismiss.error} />
        </div>
      )}
    </Card>
  );
}
